import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, MapPin, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";

export default function CarrierSite() {
  const { code } = useParams();
  const [carrier, setCarrier] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/sites/${code}`).then(({ data }) => setCarrier(data)).catch(() => setMissing(true));
  }, [code]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center" data-testid="carrier-site-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Carrier site not found</p>
      </div>
    );
  }
  if (!carrier) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white font-body" data-testid="carrier-site">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400" data-testid="carrier-site-code">
            {carrier.carrier_code}{carrier.mc_number ? ` · MC ${carrier.mc_number}` : ""}
          </span>
          {carrier.email && (
            <a href={`mailto:${carrier.email}`} data-testid="carrier-site-contact-nav" className="font-mono text-[10px] uppercase tracking-[0.25em] bg-white text-black px-4 py-2 hover:bg-neutral-300 transition-colors duration-300">
              Contact
            </a>
          )}
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-24 pb-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-[11px] uppercase tracking-[0.35em] text-neutral-500 mb-6">
          Motor Carrier Services
        </motion.p>
        <h1
          className={`font-display font-extrabold uppercase leading-[0.92] tracking-tighter sm:text-7xl lg:text-8xl ${
            Math.max(...carrier.company.split(" ").map((w) => w.length)) > 12
              ? "text-[5.5vw]"
              : Math.max(...carrier.company.split(" ").map((w) => w.length)) > 9
              ? "text-[7.5vw]"
              : "text-[9.5vw]"
          }`}
          data-testid="carrier-site-name"
        >
          <MaskedLine delay={0.35}>{carrier.company}</MaskedLine>
        </h1>
        {carrier.owner_name && (
          <Reveal delay={0.6}>
            <p className="mt-6 text-neutral-400 text-lg">Operated by {carrier.owner_name}</p>
          </Reveal>
        )}
        <Reveal delay={0.7}>
          <div className="mt-10 flex flex-wrap gap-3">
            {carrier.equipment && (
              <span className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300" data-testid="carrier-site-equipment">
                <Truck size={13} /> {carrier.equipment}
              </span>
            )}
            {carrier.preferred_lanes && (
              <span className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300" data-testid="carrier-site-lanes">
                <MapPin size={13} /> {carrier.preferred_lanes}
              </span>
            )}
            {carrier.mc_number && (
              <span className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300">
                <ShieldCheck size={13} /> Authorized · MC {carrier.mc_number}
              </span>
            )}
          </div>
        </Reveal>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-20 grid sm:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl leading-tight">
              Ready to move your freight.
            </h2>
            <p className="text-neutral-400 mt-4 leading-relaxed">
              Dependable, professional, and dispatched with purpose. Reach out to discuss lanes, capacity, and rates.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-3">
              {carrier.email && (
                <a href={`mailto:${carrier.email}`} data-testid="carrier-site-email" className="group flex items-center justify-between border border-white/15 px-5 py-4 hover:bg-white hover:text-black transition-colors duration-300">
                  <span className="font-mono text-xs tracking-[0.15em]">{carrier.email}</span>
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
              {carrier.phone && (
                <a href={`tel:${carrier.phone}`} data-testid="carrier-site-phone" className="group flex items-center justify-between border border-white/15 px-5 py-4 hover:bg-white hover:text-black transition-colors duration-300">
                  <span className="font-mono text-xs tracking-[0.15em]">{carrier.phone}</span>
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
            © {new Date().getFullYear()} {carrier.company}
          </p>
          <a href="/" data-testid="carrier-site-ph-credit" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-white transition-colors duration-300">
            Dispatched by Purpose Haul Dispatch
          </a>
        </div>
      </footer>
    </div>
  );
}
